import { Formik } from "formik";
import { useState } from "react";

import axios from "axios";
import clsx from "clsx";
import Yup from "@/base/utils/yup-extended";
import { Button } from "../../Button/Button";
import Icon from "../../Icon";
import Input from "../../Input";
import LinkComponent from "../../LinkComponent";
import { RemarkRichTextComponents } from "../RichText";
import Success from "./Success";
import * as styles from "./styles.module.scss";
import { MOOSEND_NEWSLETTER_URL } from "./utils/newsletterUrl";

export function RichTextSubscriberForm({
  heading,
  placeholder,
  title,
  links,
  variant = "",
  Mtitle = "",
}: SubscriberFormComponent) {
  const INITIAL_STATE = {
    message: "",
    isSuccess: false,
  };

  const FORM_VALUE = {
    email: "",
  };

  const [subscribe, setSubscribe] = useState(INITIAL_STATE);

  const [isMessageVisible, setIsMessageVisible] = useState(true);

  const showMessage = () => {
    // eslint-disable-next-line prefer-const
    let timer;
    clearTimeout(timer);
    timer = setTimeout(function () {
      setIsMessageVisible(false);
      setSubscribe((p) => {
        return {
          ...p,
          isSuccess: false,
        };
      });
    }, 5000);
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address.")
      .required("Field is required.")
      .emailExists("Email already exists."),
  });

  return (
    <>
      {!subscribe.isSuccess ? (
        <div
          className={clsx({
            [styles.wrapper]: variant !== "modal",
          })}
        >
          {variant !== "modal" && (
            <>
              <Icon name="cacao_pod" className={styles.adornment} />
              <h4>{title}</h4>
            </>
          )}

          <h2>
            {typeof heading !== "string" && heading?.content && (
              <RemarkRichTextComponents content={heading} />
            )}
          </h2>

          {variant === "modal" && (
            <div className={styles.headingWrapper}>
              <h2 className={styles.mtitle}> {Mtitle.split(" ")[0]}</h2>
              <h2 className={styles.mtitle}>
                {Mtitle.split(" ").slice(1).join(" ")}
              </h2>
              <h5 className={styles.update}>
                Don't miss out on the latest research on plant-based eating and
                updates from Alpro Foundation.
              </h5>
            </div>
          )}

          <Formik
            initialValues={FORM_VALUE}
            validationSchema={validationSchema}
            onSubmit={async (values, formikHelpers) => {
              try {
                formikHelpers.setStatus({ success: true });
                formikHelpers.setSubmitting(false);
                const info = await axios.post(MOOSEND_NEWSLETTER_URL, {
                  email: values.email,
                });
                showMessage();
                if (info.status === 200) {
                  switch (info.data.Code) {
                    case 0: {
                      if (typeof window !== "undefined") {
                        window.localStorage.setItem(
                          "showSubscriptionModal",
                          JSON.stringify(true)
                        );
                      }

                      return setSubscribe((prev) => {
                        return {
                          ...prev,
                          isSuccess: true,
                          message: `Thank you ${info?.data?.Context.Email}. You have successfully subscribed to our quarterly newsletter`,
                        };
                      });
                    }

                    case -1:
                      return setSubscribe((prev) => ({
                        ...prev,
                        isSuccess: false,
                        message:
                          "This email address already exists on our mailing list!",
                      }));

                    default:
                      return null;
                  }
                }
              } catch (error) {
                setSubscribe((prev) => ({
                  ...prev,
                  isSuccess: false,
                  message: "Something went wrong",
                }));

                formikHelpers.setStatus({ success: false });
                formikHelpers.setSubmitting(false);
              }
            }}
          >
            {(formikProps) => {
              return (
                <form onSubmit={formikProps.handleSubmit}>
                  <Input
                    error={
                      formikProps.touched.email && formikProps.errors.email
                    }
                    placeholder="e.g. john@example.com"
                    type="text"
                    onChange={formikProps.handleChange}
                    value={formikProps.values.email}
                    name="email"
                    labelText={placeholder}
                    fullWidth
                  />

                  <div className={styles.links}>
                    {variant !== "modal" &&
                      links?.map((link: Record<string, string>) => (
                        <>
                          <LinkComponent
                            type="internal"
                            url={`/${link.url}`}
                            key={link._uid}
                          >
                            {link.title}
                          </LinkComponent>
                        </>
                      ))}
                  </div>

                  {isMessageVisible && (
                    <p
                      className={clsx({
                        [styles.error]: !subscribe?.isSuccess,
                        [styles.success]: subscribe?.isSuccess,
                      })}
                    >
                      {subscribe?.message}
                    </p>
                  )}

                  <Button
                    variant="primary"
                    type="submit"
                    className={clsx({
                      [styles.fullWidthError]:
                        formikProps.touched.email && formikProps.errors.email,
                      [styles.fullWidth]:
                        !formikProps.touched.email || !formikProps.errors.email,
                    })}
                    disabled={formikProps.isSubmitting}
                  >
                    {"Subscribe"}
                  </Button>

                  {variant === "modal" && (
                    <p className={styles.unsubscribe}>
                      You can unsubscribe from the newsletter at any time
                    </p>
                  )}

                  <p className={styles.privacyPolicy}>
                    View our{" "}
                    <LinkComponent
                      type="external"
                      url="https://alprofoundation.org/privacy-policy"
                      title="Privacy Policy"
                    >
                      Privacy Policy
                    </LinkComponent>
                  </p>
                </form>
              );
            }}
          </Formik>
        </div>
      ) : (
        <Success />
      )}
    </>
  );
}
